import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { SuggestionRequest, SuggestionRequestStatus, VisualGeneratePrompt, VisualGenerateRequest } from 'types'
import { UseMutationOptions } from 'react-query/types/react/types'
import { useSourceMaterials } from 'hooks/useSourceMaterials'
import { Variables as GenerateDesignVariables, useGenerateDesign } from 'hooks/queries/workspaces/useGenerateDesign'

interface Props {
  workspaceId: number
}

export type Data = SuggestionRequest<VisualGeneratePrompt>['almanac_file']

export interface Variables extends GenerateDesignVariables {
  source_material_ids?: Array<number>
}

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

export const useCreateDesign = ({ workspaceId }: Props, options?: Options) => {
  const sourceMaterials = useSourceMaterials()
  const { mutateAsync } = useGenerateDesign({ workspaceId })

  return useMutation<Data, unknown, Variables>(
    'createDesign',
    async (variables) => {
      let interval = 500

      let { request } = await mutateAsync({
        ...variables,
        reference_text: (sourceMaterials.data || [])
          .filter(({ id }) => variables.source_material_ids?.includes(id))
          .map(({ content }) => content)
          .join('\n'),
      })

      do {
        const res = await sendToApi<{ request: VisualGenerateRequest }>(`/ai_suggestion_requests/${request.id}`)
        if (res.type !== SUCCESS_RESPONSE_TYPE) {
          throw new Error()
        }
        request = res.data.request
        if (request?.status === 'failed') {
          throw new Error('Generation failed')
        }
        await new Promise((resolve) => {
          setTimeout(resolve, interval)
        })
      } while (
        ![SuggestionRequestStatus.Successful, SuggestionRequestStatus.Failed, SuggestionRequestStatus.Aborted].includes(
          request.status,
        )
      )

      return request.almanac_file
    },
    {
      ...options,
    },
  )
}
